import { useDiscountCodeCampaign } from '@/hooks/discount-code/useDiscountCodeCampaign';
import { useValidateDiscountCode } from '@/hooks/discount-code/useValidateDiscountCode';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type AddDiscountCodeInputModel = {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onApply: () => void;
  isApplying: boolean;
  validated?: boolean;
  helperMessage?: string;
};

export type UseAddDiscountCodeResult = {
  expanded: boolean;
  toggleExpanded: () => void;
  input: AddDiscountCodeInputModel;
};

/**
 * A hook to manage discount code entry, validation, and URL synchronization.
 *
 * It handles:
 * 1. Initialising state from the `promocode` URL parameter.
 * 2. Automatic seeding of campaign codes via {@link useDiscountCodeCampaign}.
 * 3. Validation logic via {@link useValidateDiscountCode}.
 * 4. Updating the URL when a valid code is applied.
 *
 * @returns UI state and event handlers for the discount interface.
 */
export function useAddDiscountCode(): UseAddDiscountCodeResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const [expanded, setExpanded] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState(searchParams.get('promocode')?.toLocaleUpperCase() || '');
  const [appliedCode, setAppliedCode] = useState(searchParams.get('promocode') || '');

  const seedDefaultCampaignCode = useCallback(
    (code: string) => {
      setAppliedCode(code);
      setDiscountCodeInput(code);

      const next = new URLSearchParams(searchParams);
      next.set('promocode', code);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useDiscountCodeCampaign({ onSeedDefaultCode: seedDefaultCampaignCode });

  const { isValidated, isLoading, data: response, errorMessage, refetch } = useValidateDiscountCode(appliedCode);

  useEffect(() => {
    if (response?.status === 200) {
      const validCode = response.body.data.discountCode;

      if (searchParams.get('promocode') !== validCode) {
        const next = new URLSearchParams(searchParams);
        next.set('promocode', validCode);
        setSearchParams(next, { replace: true });
      }

      setDiscountCodeInput(validCode);
      setAppliedCode(validCode);

      if (!expanded) {
        setExpanded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, setSearchParams]);

  const apply = useCallback(() => {
    const cleanCode = discountCodeInput.trim().toLocaleUpperCase();
    if (!cleanCode) {
      return;
    }

    if (cleanCode === appliedCode) {
      void refetch();
    } else {
      setAppliedCode(cleanCode);
    }
  }, [discountCodeInput, appliedCode, refetch]);

  return {
    expanded,
    toggleExpanded: () => setExpanded((expanded) => !expanded),
    input: {
      value: discountCodeInput,
      onChange: (e) => setDiscountCodeInput(e.target.value.toLocaleUpperCase()),
      onKeyDown: (e) => e.key === 'Enter' && apply(),
      onApply: apply,
      isApplying: isLoading,
      validated: isValidated,
      helperMessage: errorMessage ?? (response?.status === 200 ? response.body.data.message : undefined),
    },
  };
}
